import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
	WCB_ALL_PROVINCES_LIST_SCREEN_SLUG,
} from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatAmountCommaSeperator, formatDate, formatThousandSeperatorWithTwoFixedAmount, savePdfAsFile, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import FileSaver from "file-saver";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../models";
import { WCBAllProvincesRemittanceDataGrid } from "./WCBAllProvincesRemittanceDataGrid";
import { WCBAllProvincesRemittanceHeader } from "./WCBAllProvincesRemittanceHeader"
import { WCBService } from "../../services/WCBService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

export interface IWCBAllProvincesRemittance { }

const useWCBAllProvincesRemittanceSavedFrequency = (
	wcbProvinceSlug: string,
	clientId: string,
	taxYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			'wcb-saved-frequency', wcbProvinceSlug
		],
		queryFn: (data) => {

			return WCBService.getWCBAllProvincesRemittanceSavedFrequency(
				wcbProvinceSlug,
				clientId,
				taxYear,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetRemittanceFrequencyDDL = (
	provinceId: string | null
) =>
	useQuery({
		queryKey: ["remittance-frequency-ddl", provinceId],
		queryFn: (data) => {
			return WCBService.getRemittanceFrequencyDDL(provinceId);
		},
		refetchOnWindowFocus: false,
	});
const useGetSavedWCBAllProvincesDDL = (
	clientId: string,
	provinceId: string | null,
) =>
	useQuery({
		queryKey: ["saved-wcb-ddl", clientId, provinceId],
		queryFn: (data) => {
			return WCBService.getSavedWCBAllProvincesDDL(clientId, provinceId);
		},
		refetchOnWindowFocus: false,
	});
const useGetDefaultCodeProfileDataById = (
	provinceId: string | null, id: null | string
) =>
	useQuery({
		queryKey: ["get-defaultCode-profile", id, provinceId],
		queryFn: (data) => {
			return WCBService.getDefaultCodeProfileDataById(
				// provinceId, 
				id
			);
		},
		refetchOnWindowFocus: false,
	});

// const SINGLE_RECORD_DATA_QUERY_KEY = "code-setup-single-data-record";
// const useGetDefaultCodeProfileDataById = (
// 	wcbProvinceSlug: string,
// 	provinceId: string | null,
// 	clientId: string,
// 	id: string | undefined,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id,wcbProvinceSlug, provinceId, navigateToLogout],
// 		queryFn: () => {
// 			return WCBService.getAllProvincesCodeById(wcbProvinceSlug,provinceId, clientId, id, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const WCB_ALL_PROVINCES_LIST_QUERY_KEY = "rq-ramittance-list";

const useWCBAllProvincesRemittances = (
	wcbProvinceSlug: string,
	remittanceFrequencyId: string | undefined,
	clientId: string,
	taxYear: number,
	loadedProfileId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			WCB_ALL_PROVINCES_LIST_QUERY_KEY,
			wcbProvinceSlug,
			remittanceFrequencyId,
			clientId,
			taxYear,
			loadedProfileId,
			navigateToLogout
		],
		queryFn: (data) => {

			return WCBService.getWCBAllProvincesRemittance(
				wcbProvinceSlug,
				remittanceFrequencyId,
				clientId,
				taxYear,
				loadedProfileId,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetDefaultCodeDDL = (wcbProvinceSlug: any, clientId: any, provinceId: any, taxYear: number) =>
	useQuery({
		queryKey: ["default-code-ddl", wcbProvinceSlug, clientId, provinceId, taxYear],
		queryFn: (data) => {
			return WCBService.getDefaultCodeDDL(wcbProvinceSlug, clientId, provinceId, taxYear);
		},
		refetchOnWindowFocus: false,
	});
// const useGetAllProvincesCodeDDL = (
// wcbProvinceSlug: string, provinceId: any, taxYear: number) =>
// useQuery({
// 	queryKey: ["wcb-all-provinces-code-ddl",wcbProvinceSlug, provinceId],
// 	queryFn: (data) => {
// 		return WCBService.getAllProvincesCodesDDL(wcbProvinceSlug,provinceId, taxYear);
// 	},
// 	refetchOnWindowFocus: false,
// });

export const WCBAllProvincesRemittanceSetup: React.FC<IWCBAllProvincesRemittance> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [rqRamittanceSetupTableData, setWCBAllProvincesRemittanceSetupTableData] =
		useState([]);
	const [remittanceFrequencyId, setRemittanceFrequencyId] = useState(undefined);
	const [selectedDefaultCodeProfile, setSelectedDefaultCodeProfile] = useState<any>(null);
	const [isPendingData, setIsPendingData] = useState(false);

	const [provinceId, setProvinceId] = useState<any>(null);
	const [loadedProfileId, setLoadedProfileId] = useState<any>(null);
	const { wcbProvinceName } = useParams();

	const [wcbProvinceSlug, setWCBProvinceSlug] = useState<any>(null);
	const [isLoading, setIsLoading] = useState<any>(false);
	useEffect(() => {
		if (wcbProvinceName && wcbProvinceName.length > 0) {

			setWCBProvinceSlug(wcbProvinceName);
			// if (wcbProvinceName !== wcbProvinceSlug) {
			// setEditRecordId(undefined)
			// }
		}
	}, [wcbProvinceSlug, wcbProvinceName]);

	useEffect(() => {
		if (wcbProvinceSlug) {
			formMethods.reset();
			// formMethods.setValue("defaultCode", null)
		}
	}, [wcbProvinceSlug]);

	const canadaProvince = useSelector((state: IAppState) => state.countryAndProvincesInfo.canadaProvinces)
	useEffect(() => {
		const updatedCanadaProvincesLabels = canadaProvince && canadaProvince.map((obj: any) => ({ ...obj, label: obj.label.toLowerCase().replace(/\s+/g, '_') }));
		const currentLoadedProvince = updatedCanadaProvincesLabels?.find((x: any) => x.label === wcbProvinceSlug)
		if (currentLoadedProvince) {
			setProvinceId(currentLoadedProvince.value)
		}
	}, [wcbProvinceSlug, canadaProvince]);

	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});
	const remittanceFrequencyIdField = formMethods.getValues(
		"remittanceFrequencyId"
	);
	useEffect(() => {
		if (remittanceFrequencyIdField) {
			setRemittanceFrequencyId(remittanceFrequencyIdField);
		}
	}, [remittanceFrequencyIdField]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { isPending: isPendingWCBAllProvincesRemittanceSavedFrequency, data: wcbSavedFrequency }: any = useWCBAllProvincesRemittanceSavedFrequency(
		wcbProvinceSlug,
		clientIdStore,
		taxYear,
		navigateToLogout
	);

	const {
		isPending: isPendingSavedWCBAllProvincesDDL,
		data: savedWCBAllProvincesDDL,
	} = useGetSavedWCBAllProvincesDDL(clientIdStore, provinceId);

	useEffect(() => {
		// if (wcbSavedFrequency && wcbSavedFrequency.frequencies !== null) {
		if (wcbSavedFrequency) {
			formMethods.setValue(
				"remittanceFrequencyId",
				wcbSavedFrequency.frequencies
			);
			setRemittanceFrequencyId(wcbSavedFrequency.frequencies);

			if (wcbSavedFrequency.frequencies && savedWCBAllProvincesDDL && !loadedProfileId) {
				const latestItem = savedWCBAllProvincesDDL.length
					? savedWCBAllProvincesDDL
						.filter((item: any) => !(item.value === '' && item.label === '--'))
						.reduce((latest: any, item: any) =>
							Date.parse(item.label) > Date.parse(latest.label) ? item : latest
						)
					: null;

				if (latestItem) {
					formMethods.setValue(
						"savedWCBAllProvincesDDL",
						latestItem.value
					);
					setLoadedProfileId(latestItem.value)
				}
			}
		}
	}, [wcbSavedFrequency, savedWCBAllProvincesDDL]);

	const { id } = useSelector((state: IAppState) => state.accountant);
	const { isPending, data } = useWCBAllProvincesRemittances(
		wcbProvinceSlug,
		remittanceFrequencyId,
		clientIdStore,
		taxYear,
		loadedProfileId,
		navigateToLogout
	); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const wcbAllProvincesRemittanceSetup: any | null = data;




	const { isPending: pendingDefaultCodeProfileDAta, data: defaultCodeProfileData } = useGetDefaultCodeProfileDataById(
		provinceId,
		loadedProfileId);


	useEffect(() => {
		if (defaultCodeProfileData) {
			const effectiveDate = new Date(defaultCodeProfileData.effectiveDate)
			formMethods.setValue("effectiveDate", effectiveDate)
			formMethods.setValue("defaultCode", defaultCodeProfileData.defaultCode)
			if (defaultCodeProfileData.defaultCode && defaultCodeProfileData.defaultCode === 'new-rate') {
				formMethods.setValue("rate", defaultCodeProfileData.rate)
				formMethods.setValue("description", defaultCodeProfileData.description)
			} else {
				formMethods.setValue("rate", null)
				formMethods.setValue("description", "")
			}
		}
	}, [defaultCodeProfileData])

	const {
		isPending: isPendingRemittanceFrequencyDDL,
		data: remittanceFrequencyDDL,
	} = useGetRemittanceFrequencyDDL(provinceId);


	const updatedRemittanceFrequencyDDL = useMemo(() => {
		if (!remittanceFrequencyDDL) return [];

		const remappedDDL = remittanceFrequencyDDL
			.map((item: any) => {
				switch (item.label) {
					case "Regular":
						return { ...item, label: "Monthly" };
					case "Threshold 1":
						return { ...item, label: "Semi Monthly" };
					case "Threshold 2":
						return { ...item, label: "Weekly" };
					default:
						return item;
				}
			});
		const provinceFrequencyMap: Record<string, string[]> = {
			alberta: ["Annual"],
			british_columbia: ["Annual", "Quarterly"],
			manitoba: ["Annual", "Quarterly", "Monthly"],
			new_brunswick: ["Annual", "Monthly"],
			newfoundland_and_labrador: ["Weekly", "Bi Weekly", "Monthly", "Semi Monthly", "Quarterly"],
			northwest_territories: ["Quarterly"],
			nova_scotia: ["Monthly", "Quarterly", "Semi Monthly", "Weekly"],
			nunavut: ["Quarterly"],
			prince_edward_island: ["Annual", "Monthly", "Semi Annual"],
			saskatchewan: ["Annual", "Quarterly", "Semi Annual"],
			yukon: ["Annual", "Quarterly", "Monthly", "Semi Annual", "Bi Weekly", "Weekly"],
		};
		const allowedLabels = provinceFrequencyMap[wcbProvinceSlug ?? ""];
		if (!allowedLabels || allowedLabels.length === 0) return remappedDDL;
		return remappedDDL.filter((item: any) =>
			allowedLabels.includes(item.label)
		);

	}, [remittanceFrequencyDDL]);


	// remittanceFrequencyDDL = [ { "label": "Quarterly", "value": "682c7edc2e9342bfbf76c9dd" }, { "label": "Regular", "value": "682c80e4561a8d3349bcd85f" }, { "label": "Threshold 1", "value": "682c819747f56c520f0058b3" }, { "label": "Threshold 2", "value": "682c81e447f56c520f0058b5" } ] const updatedRemittanceFrequencyDDL = useMemo(() => { if (remittanceFrequencyDDL) { } }, [remittanceFrequencyDDL]) i want to change label Regular with Monthly and Threshold 1 with Semimonthly and Threshold 2 with Weekly

	// const {
	// 	isPending: isPendingDefaultCodeDDL,
	// 	data: defaultCodeDDL,
	// } = useGetAllProvincesCodeDDL(wcbProvinceSlug,provinceId, taxYear);
	const {
		isPending: isPendingDefaultCodeDDL,
		data: defaultCodeDDL,
	} = useGetDefaultCodeDDL(wcbProvinceSlug, clientIdStore, provinceId, taxYear);

	const updateddefaultCodeDDL = useMemo(() => {
		if (defaultCodeDDL) {
			if (defaultCodeDDL && Array.isArray(defaultCodeDDL)) {
				let defaultCode: IDropDownListResponse[] = sortDDLByLabel(defaultCodeDDL).map(
					(defaultCodeItem: IDropDownListResponse) => ({
						label: defaultCodeItem.label,
						value: defaultCodeItem.value,
					})
				);
				defaultCode.unshift({
					label: "New Rate",
					value: "new-rate",
				});
				return defaultCode;
			}
		}
	}, [defaultCodeDDL])



	const { isPending: isSubmitting, mutate: submitWCBAllProvincesRemittance } = useMutation({
		mutationFn: WCBService.createWCBAllProvincesRemittance,

		onSuccess: (res: IWCBAllProvincesRemittance | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				queryClient.invalidateQueries({
					queryKey: [WCB_ALL_PROVINCES_LIST_QUERY_KEY],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingDefaultCodeSubmit, mutate: submitWCBAllProvincesRemittanceDefaultCodeValues } = useMutation({
		mutationFn: WCBService.createDefaultCode,

		onSuccess: (res: IWCBAllProvincesRemittance | IServerError | IResponse | any) => {


			if ((res && res.status === 201) || (res && res.status === 200)) {
				// queryClient.invalidateQueries({
				// 	queryKey: ["default-code-ddl"],
				// });
				queryClient.invalidateQueries({
					queryKey: ["saved-wcb-ddl"],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// setSelectedDefaultCodeProfile(res.data.id)
				setLoadedProfileId(res.data.id)
				formMethods.setValue('savedWCBAllProvincesDDL', res.data.id)
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { message, componentState } = useSelector(
		(state: IAppState) => state.viewState
	);

	// const { accountantList } = useSelector(
	//   (state: IAppState) => state.accountant
	// );

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroWCBAllProvincesRemittance, setzeroWCBAllProvincesRemittance] = useState(true);
	const [rqRamittanceSetupUpdatedItems, setWCBAllProvincesRemittanceSetupUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [rqRamittanceRowData, setWCBAllProvincesRemittanceRowData] = useState<any>(0);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroWCBAllProvincesRemittance(false);
	};

	const { isPending: isDeleting, mutate: deleteWCBAllProvincesRemittanceById } =
		useMutation({
			mutationFn: WCBService.delWCBAllProvincesRemittance,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the WCBAllProvincesRemittance fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					queryClient.invalidateQueries({
						queryKey: ["default-code-ddl"],
					});
					formMethods.setValue("rate", null)
					formMethods.setValue("effectiveDate", null)
					formMethods.setValue("defaultCode", 'new-rate')
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				return queryClient.invalidateQueries({
					queryKey: [WCB_ALL_PROVINCES_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteDepartmentsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				id: selectedDefaultCodeProfile,
				navigateToLogout: navigateToLogout,
			};
			deleteWCBAllProvincesRemittanceById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setWCBAllProvincesRemittanceRowData(params);
	}, []);

	const onSavedDefaultCodeSelectionChangeHandler = useCallback((params: any) => {
		setSelectedDefaultCodeProfile(params.value)
	}, []);

	const onDefaultCodeDeleteClicked = useCallback(() => {
		setDialogOpen(true);
		setSingleDelete(true);
	}, []);

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/rqRamittance/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		if (selectedItems.length) {
			return (
				// <Box sx={{ marginTop: theme.spacing(1) }}>
				// 	<Button
				// 		onClick={() => onRowMultipleDeleteClicked(selectedItems)}
				// 		variant="contained"
				// 		color="error"
				// 	>
				// 		Delete
				// 	</Button>
				// </Box>
				<></>
			);
		}
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton to="/rqRamittance/setup" text="Add WCBAllProvincesRemittance" />
			// </Box>
			<></>
		);
	}, []);
	const updatedWCBAllProvincesRemittanceRamittanceSetupItems = useMemo(() => {
		if (wcbAllProvincesRemittanceSetup && wcbAllProvincesRemittanceSetup.items) {
			const items = [...wcbAllProvincesRemittanceSetup.items];

			// Find the last item with a non-null remitAmount using binary search
			let left = 0;
			let right = items.length - 1;
			let lastRemittedIndex = -1;

			while (left <= right) {
				let mid = Math.floor((left + right) / 2);
				if (items[mid].remitAmount != null) {
					lastRemittedIndex = mid;
					left = mid + 1; // look for later remitted value
				} else {
					right = mid - 1;
				}
			}

			// Find the first null remitAmount AFTER the last remitted one
			let nextNullIndex = -1;
			for (let i = lastRemittedIndex + 1; i < items.length; i++) {
				if (items[i].remitAmount == null) {
					nextNullIndex = i;
					break;
				}
			}
			// Map and add the two tags
			return items.map((item, index) => ({
				...item,
				// canDelete:
				// 	items[0].remitAmount === null ? false : index === lastRemittedIndex,
				// canEdit: items[0].remitAmount === null ? true : index === nextNullIndex,
				canDelete: index === lastRemittedIndex,
				canEdit: index === nextNullIndex,
				insurableEarnings: formatAmountCommaSeperator(item.insurableEarnings),
				balanceShortExcess: formatAmountCommaSeperator(item.balanceShortExcess)
			}));
		}
	}, [wcbAllProvincesRemittanceSetup]);



	const expandedLabels = ["WCB/WSIB", `${canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label}`];
	const expandedWCBAllProvincesRemittanceSetupItem = "Remittance"; // Special case for "List" under "WCBAllProvincesRemittance"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === `${canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label}` &&
				item.label === expandedWCBAllProvincesRemittanceSetupItem
			) {
				isExpanded = true;
			}
			let itemPath = item.path;

			if (
				parentLabel === `${canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === wcbProvinceSlug)?.label}`
				&& wcbProvinceSlug
				&& item.label === expandedWCBAllProvincesRemittanceSetupItem
			) {
				itemPath = `/taxes/wcb/remittance/${wcbProvinceSlug}`;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: itemPath
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: WCB_ALL_PROVINCES_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions, wcbProvinceSlug]);

	const onSubmitButtonClickedHandler = () => {


		const updatePayloadData =
			rqRamittanceSetupTableData &&
			rqRamittanceSetupTableData.map((item: any) => ({
				...item,
				wcbProvinceSlug: wcbProvinceSlug,
				chequeDate: formatDate(item.chequeDate),
			}));

		const EditedRowData = updatePayloadData.filter((item: any) => item.canEdit)
		submitWCBAllProvincesRemittance(
			{
				data: { data: [EditedRowData[0]] },
				wcbProvinceSlug: wcbProvinceSlug,
				navigateToLogout: navigateToLogout
			}
		)
	};

	const onSubmit = (values: any) => {

		console.log("valuesvaluesvalues", values)
		// let payload = {
		// 	id: id && id,
		// 	rate: values.rqRate,
		// 	effectiveDate: values.effectiveDate && formatDate(values.effectiveDate),
		// }

		// submitWCBAllProvincesRemittance(payload)
	};


	const onSubmitDefaultCodeHandler = () => {
		const values = formMethods.watch();
		const values1 = formMethods.watch([
			"defaultCode",
			"descriptions",
			"customRate",
			"effectiveDate"
		]);
		console.log('valuesvalues', values)
		console.log('valuesvalues', values1)
		if (values.defaultCode === 'new-rate' && !values.rate) {
			setSnackbarData({
				open: true,
				message: "Rate is required",
				severity: "error"
			})
		} else if (values.defaultCode === 'new-rate' && !values.description) {
			setSnackbarData({
				open: true,
				message: "Description is required",
				severity: "error"
			})
		} else if (!values.effectiveDate || !values.effectiveDate) {
			setSnackbarData({
				open: true,
				message: "Effective Date is required",
				severity: "error"
			})
		} else {
			let payload = {
				id: defaultCodeProfileData && defaultCodeProfileData?.effectiveDate === formatDate(values.effectiveDate) ? defaultCodeProfileData.id : undefined,
				// rate: "121211111",
				// effectiveDate: "2024-11-11"
				clientId: clientIdStore,
				provinceId: provinceId,
				defaultCode: values.defaultCode,
				description: values.description,
				rate: values.rate,
				effectiveDate: values.effectiveDate && formatDate(values.effectiveDate),
			}

			submitWCBAllProvincesRemittanceDefaultCodeValues(payload)
		}
	}

	console.log("wcbProvinceSlugwcbProvinceSlug111", wcbProvinceSlug)
	const onRemittanceFrequencyChangeHandler = async (selectedItem: any) => {
		if (wcbSavedFrequency && wcbSavedFrequency.frequencies === null) {
			(async () => {
				setIsPendingData(true)
				try {
					const responce = await WCBService.createWCBAllProvincesRemittanceFrequency(
						wcbProvinceSlug,
						selectedItem.value,
						clientIdStore,
						taxYear,
						loadedProfileId,
						navigateToLogout
					)

				}
				catch {
					setIsPendingData(false)
				} finally {
					setIsPendingData(false)
					queryClient.invalidateQueries({
						queryKey: [
							'wcb-saved-frequency'
						],
					});
					setRemittanceFrequencyId(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							WCB_ALL_PROVINCES_LIST_QUERY_KEY,
							wcbProvinceSlug,
							selectedItem.value,
							clientIdStore,
							taxYear,
							loadedProfileId,
						],
					});
				}
			})();
		} else if (selectedItem.value) {
			// setRemittanceFrequencyId(selectedItem.value);
			// queryClient.invalidateQueries({
			// 	queryKey: [
			// 		WCB_ALL_PROVINCES_LIST_QUERY_KEY,
			// 		clientIdStore,
			// 		selectedItem.value,
			// 		managedBy,
			// 		taxYear,
			// 	],
			// });
			(async () => {
				setIsPendingData(true)
				try {
					const responce = await WCBService.createWCBAllProvincesRemittanceFrequency(
						wcbProvinceSlug,
						selectedItem.value,
						clientIdStore,
						taxYear,
						loadedProfileId,
						navigateToLogout
					)

				}
				catch {
					setIsPendingData(false)
				} finally {
					setIsPendingData(false)
					setRemittanceFrequencyId(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							WCB_ALL_PROVINCES_LIST_QUERY_KEY,
							wcbProvinceSlug,
							selectedItem.value,
							clientIdStore,
							taxYear,
							loadedProfileId,
						],
					});
				}
			})();
		}
	};

	const onSavedWCBChangeHandler = async (event: any) => {
		if (!event.value) {
			formMethods.setValue("rate", null)
			formMethods.setValue("description", "")
			formMethods.setValue("effectiveDate", null)
			formMethods.setValue("defaultCode", 'new-rate')
			setLoadedProfileId(event.value)
		} else {
			(async () => {
				setIsPendingData(true)
				try {
					const responce = await WCBService.createWCBAllProvincesRemittanceFrequency(
						wcbProvinceSlug,
						remittanceFrequencyId,
						clientIdStore,
						taxYear,
						event.value,
						navigateToLogout
					)

				}
				catch {
					setIsPendingData(false)
				} finally {
					setIsPendingData(false)
					queryClient.invalidateQueries({
						queryKey: [
							'wcb-saved-frequency'
						],
					});
					setLoadedProfileId(event.value)
				}
			})();
		}
	}



	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"remittanceFrequencyId",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};
	const onExportButtonClick = async () => {
		const [remittanceFrequencyId] = values;
		try {
			setIsLoading(true);
			const response = await WCBService.exportWCBRemittanceExcelReport(
				wcbProvinceSlug,
				clientIdStore,
				remittanceFrequencyId,
				taxYear,
				loadedProfileId,
			);

			if (response) {
				if (response.status === 400) {
					setSnackbarData({
						open: true,
						message: response.data.message
							? response.data.message
							: "Something went wrong.",
						severity: "error",
					});
				} else {
					const filename = `WCB ${wcbProvinceSlug} Remittance.xlsx`;
					savePdfAsFile(response, filename);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {
			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

				sx={{
					mt: "1rem"
				}}>
				<div
					style={{
						margin: "15px 0",
						fontSize: "1.5rem",
						fontWeight: "bold",
						fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
					}}
				>
					{"Remittance"}
				</div>
				<BackdropCircularProgress
					open={
						isPending ||
						isPendingRemittanceFrequencyDDL ||
						isSubmitting ||
						isPendingWCBAllProvincesRemittanceSavedFrequency ||
						isPendingDefaultCodeDDL ||
						isPendingDefaultCodeSubmit ||
						pendingDefaultCodeProfileDAta ||
						isPendingSavedWCBAllProvincesDDL ||
						isPendingData || isLoading
					}
					testId="rqRamittance-list"
				/>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={deleteDepartmentsField}
					cancelActionHandler={onCloseHandler}
					testId="wcb-remittance-confirmation-dialog"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<WCBAllProvincesRemittanceHeader
							defaultCodeDDL={updateddefaultCodeDDL ?? []}
							onSubmitDefaultCodeHandler={onSubmitDefaultCodeHandler}
							onRemittanceFrequencyChangeHandler={onRemittanceFrequencyChangeHandler}
							remittanceFrequencyDDL={updatedRemittanceFrequencyDDL}
							onDefaultCodeDeleteClicked={onDefaultCodeDeleteClicked}
							onSavedDefaultCodeSelectionChangeHandler={onSavedDefaultCodeSelectionChangeHandler}
							wcbSavedFrequency={wcbSavedFrequency}
							formMethods={formMethods}
							savedWCBAllProvincesDDL={savedWCBAllProvincesDDL}
							onSavedWCBChangeHandler={onSavedWCBChangeHandler}
							setSelectedDefaultCodeProfile={(value: any) => setSelectedDefaultCodeProfile(value)}
							wcbAllProvincesRemittanceSetup={wcbAllProvincesRemittanceSetup && wcbAllProvincesRemittanceSetup}
							onExportButtonClick={onExportButtonClick}
							defaultCodeProfileData={defaultCodeProfileData}
						/>
					</form>
				</FormProvider>

				<WCBAllProvincesRemittanceDataGrid
					columns={wcbAllProvincesRemittanceSetup ? wcbAllProvincesRemittanceSetup.headerColumns : []}
					rows={
						wcbAllProvincesRemittanceSetup && updatedWCBAllProvincesRemittanceRamittanceSetupItems
							? updatedWCBAllProvincesRemittanceRamittanceSetupItems
							: []
					}
					showIndexColumn={true}
					showChecboxColumn={isAdminUser === 400 ? true : false}
					// showSettingColumn={true}
					// onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wcbAllProvincesRemittanceSetup && wcbAllProvincesRemittanceSetup.total
							? wcbAllProvincesRemittanceSetup.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					searchFieldPlaceholder={"Search..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroWCBAllProvincesRemittance(value)
					}
					pageTitle={"WCB Remittance"}
					wcbSetupTableData={(data: any) =>
						setWCBAllProvincesRemittanceSetupTableData(data)
					}
					tableMargin={"0"}
				/>



				{wcbAllProvincesRemittanceSetup && wcbAllProvincesRemittanceSetup.items && (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							// backgroundColor: "#fff",
							backgroundColor: "#F7F7F7",
							// marginTop: theme.spacing(3),
							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					>
						<Toolbar>
							<EnhancedButtonPrimeReact
								label="Submit"
								// type="submit"
								onClickHandler={onSubmitButtonClickedHandler}
								// icon="pi pi-check"
								raised
								severity={EnhancedButtonPrimeReactSeverity.Success}
							/>
						</Toolbar>
					</AppBar>
				)}
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
